import { cn } from '@/lib/tailwind-utils.js';
import { ReactNode, useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from './sheet.js';
import { Dialog, DialogHeader, DialogDescription, DialogContent, DialogFooter, DialogTitle } from './dialog.js';
import { Button } from './button.js';

export interface RightDrawerChildrenProps {
  closeDrawer: (bypassConfirmation?: boolean) => void;
  setShouldConfirmOnClose: (value: boolean) => void;
  showConfirmation: () => void;
}

interface RightDrawerBaseProps {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode | ((props: RightDrawerChildrenProps) => ReactNode);
  /**
   * Optional tailwind class for customizing the drawer width
   * @example "w-[900px] min-w-[40%]"
   */
  widthClass?: string;
  /**
   * Optional tailwind class for customizing the content wrapper below the header
   */
  contentClassName?: string;
  dismissible?: boolean;
  shouldConfirmOnCloseModal?: boolean;
  confirmationTitle?: string;
  confirmationDescription?: string;
  confirmationConfirmText?: string;
  confirmationCancelText?: string;
  hideCloseButton?: boolean;
  /**
   * Callback called before closing when clicking outside. Return true to allow close, false to prevent.
   */
  onBeforeClose?: () => Promise<boolean> | boolean;
}

/**
 * Uncontrolled mode: Provide a trigger element
 */
interface RightDrawerUncontrolledProps extends RightDrawerBaseProps {
  trigger: ReactNode;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Controlled mode: Provide open state and onOpenChange callback
 */
interface RightDrawerControlledProps extends RightDrawerBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type RightDrawerProps = RightDrawerUncontrolledProps | RightDrawerControlledProps;

export interface RightDrawerRef {
  close: () => void;
}

export const RightDrawer = forwardRef<RightDrawerRef, RightDrawerProps>((props, ref) => {
  const {
    title,
    description,
    children,
    widthClass,
    contentClassName,
    onOpenChange,
    dismissible = true,
    shouldConfirmOnCloseModal = false,
    confirmationCancelText = 'Stay',
    confirmationConfirmText = 'Exit',
    confirmationDescription = 'You have unsaved changes. Are you sure you want to exit?',
    confirmationTitle = 'Unsaved Changes',
    hideCloseButton = false,
    onBeforeClose,
  } = props;

  // Support both controlled and uncontrolled modes
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = 'open' in props;
  const isOpen = isControlled ? props.open : internalIsOpen;
  const trigger = isControlled ? undefined : props.trigger;
  const [shouldConfirmOnClose, setShouldConfirmOnClose] = useState(shouldConfirmOnCloseModal);
  const [isShowConfirmationModalOpen, setIsShowConfirmationModalOpen] = useState(false);

  const requestCloseConfirmation = () => {
    setIsShowConfirmationModalOpen(true);
  };

  const applyOpenChange = (newIsOpen: boolean) => {
    if (!isControlled) {
      setInternalIsOpen(newIsOpen);
    }
    onOpenChange?.(newIsOpen);
  };

  const handleOpenChange = (newIsOpen: boolean, bypassConfirmation: boolean = false) => {
    // Opening drawer or bypassing confirmation - always allow
    if (newIsOpen || bypassConfirmation) {
      applyOpenChange(newIsOpen);
      return;
    }

    // Closing drawer - check if confirmation is needed
    if (shouldConfirmOnClose) {
      requestCloseConfirmation();
      return;
    }

    applyOpenChange(newIsOpen);
  };

  const onConfirmationModalConfirm = () => {
    applyOpenChange(false);
    setIsShowConfirmationModalOpen(false);
  };

  const onConfirmationModalCancel = () => {
    setIsShowConfirmationModalOpen(false);
  };

  const onEscapeKeyDown = (event: KeyboardEvent) => {
    if (!dismissible) {
      event.preventDefault();
      return;
    }
    if (!shouldConfirmOnClose) return;
    event.preventDefault();
    requestCloseConfirmation();
  };

  const onPointerDownOutside = async (event: CustomEvent<{ originalEvent: PointerEvent }>) => {
    if (!dismissible && !onBeforeClose) {
      event.preventDefault();
      return;
    }

    // If onBeforeClose is provided, always prevent default and handle manually.
    // This allows the parent to perform async operations (like saving) before closing.
    if (onBeforeClose) {
      event.preventDefault();
      const canClose = await onBeforeClose();
      if (canClose) {
        handleOpenChange(false, true);
      }
      return;
    }

    if (!shouldConfirmOnClose) return;
    event.preventDefault();
    requestCloseConfirmation();
  };

  useImperativeHandle(ref, () => ({
    close: () => {
      if (!isControlled) {
        setInternalIsOpen(false);
      }
      onOpenChange?.(false);
    },
  }));

  useEffect(() => {
    if (!shouldConfirmOnClose) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isOpen) return;
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isOpen, shouldConfirmOnClose]);

  useEffect(() => {
    setShouldConfirmOnClose(shouldConfirmOnCloseModal);
  }, [shouldConfirmOnCloseModal]);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
        <SheetContent
          side="right"
          className={cn('w-full sm:max-w-md overflow-y-auto !pt-0', widthClass)}
          onEscapeKeyDown={onEscapeKeyDown}
          onPointerDownOutside={onPointerDownOutside}
          hideCloseButton={hideCloseButton}
        >
          <SheetHeader className="sticky top-0 z-10 pb-4 mb-4 -mx-6 px-6 pt-6 bg-[#F7F7F9]">
            <SheetTitle>{title}</SheetTitle>
            {description && <SheetDescription>{description}</SheetDescription>}
          </SheetHeader>
          <div className={cn('py-6', contentClassName)}>
            {isOpen ? (
              <>
                {typeof children === 'function'
                  ? children({
                      closeDrawer: (bypassConfirmation?: boolean) => handleOpenChange(false, bypassConfirmation),
                      setShouldConfirmOnClose: (value: boolean) => setShouldConfirmOnClose(value),
                      showConfirmation: requestCloseConfirmation,
                    })
                  : children}
              </>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
      {shouldConfirmOnClose && (
        <Dialog open={isShowConfirmationModalOpen} onOpenChange={setIsShowConfirmationModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{confirmationTitle}</DialogTitle>
              <DialogDescription>{confirmationDescription}</DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex justify-end gap-2">
              <Button onClick={onConfirmationModalConfirm}>{confirmationConfirmText}</Button>
              <Button onClick={onConfirmationModalCancel} variant={'secondary'}>
                {confirmationCancelText}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
});
